import { useRef } from "react";
import { Tiles } from "@/components/motion/Tiles";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowLeft, ArrowRight, Phone } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SpecTable } from "@/components/fleet/SpecTable";
import { VehicleCard } from "@/components/fleet/VehicleCard";
import { Gallery } from "@/components/fleet/Gallery";
import { QuoteForm } from "@/components/forms/QuoteForm";
import { Item, Lines, Reveal, Stagger } from "@/components/motion/Reveal";
import { CATEGORIES, byCategory, getVehicle } from "@/data/fleet";
import { COMPANY, whatsappUrl } from "@/data/company";

export default function Mezzo() {
  const { categoria, slug } = useParams();
  const v = getVehicle(slug);
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);

  if (!v || v.category !== categoria) return <Navigate to="/parco-mezzi" replace />;
  const cat = CATEGORIES[v.category];
  const related = byCategory(v.category).filter((x) => x.slug !== v.slug).slice(0, 3);
  const wa = whatsappUrl(`Ciao DESP, vorrei un preventivo per il noleggio di: ${v.name}. Grazie!`);

  return (
    <>
      <Seo
        title={`${v.name} a noleggio`}
        description={`${v.summary} Noleggio a Catania e Sicilia orientale con consegna in cantiere.`}
        path={`/parco-mezzi/${v.category}/${v.slug}`}
        image={v.image}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: v.name,
          brand: { "@type": "Brand", name: v.brand },
          category: `${cat.name} a noleggio`,
          description: v.summary,
          image: `${process.env.REACT_APP_SITE_URL || window.location.origin}${v.image}`,
          offers: { "@type": "Offer", availability: "https://schema.org/InStock", priceCurrency: "EUR", price: "0", description: "Noleggio giornaliero, settimanale o mensile. Preventivo su richiesta.", areaServed: "Catania, Sicilia orientale", seller: { "@type": "LocalBusiness", name: "Desp sas - DESP di Sandro Pace", telephone: "+393515837771" } },
          additionalProperty: v.specs.map((s) => ({ "@type": "PropertyValue", name: s.label, value: s.value })),
        }}
      />

      <section ref={ref} className="relative overflow-hidden bg-ink text-white" data-testid="vehicle-hero">
        <div className="relative h-[60vh] min-h-[420px] lg:h-[75vh]">
          <motion.img style={{ y: imgY, objectPosition: v.imagePosition }} src={v.image} alt={v.name} className="absolute inset-0 h-[120%] w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-ink/30" />
        </div>
        <div className="container-x relative -mt-40 pb-14 sm:-mt-48">
          <Reveal y={10} className="flex flex-wrap items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-white/50" data-testid="breadcrumbs">
            <Link to="/parco-mezzi" className="hover:text-white">Parco Mezzi</Link><span>/</span>
            <Link to={`/parco-mezzi/${cat.slug}`} className="hover:text-white">{cat.name}</Link><span>/</span>
            <span className="text-white/80">{v.model}</span>
          </Reveal>
          <div className="mt-6 grid gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <Reveal y={10} delay={0.1}><p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />{v.brand} · {v.type}</p></Reveal>
              <Lines inView={false} delay={0.15} lines={[v.brand, v.model]} className="display mt-3 text-5xl sm:text-6xl lg:text-8xl" />
              <Reveal delay={0.4} className="mt-5 max-w-xl text-base text-white/70 sm:text-lg"><p data-testid="vehicle-summary">{v.summary}</p></Reveal>
            </div>
            <Reveal delay={0.45} className="lg:col-span-4">
              <div className="grid grid-cols-3 gap-px border border-white/10 bg-white/10">
                {v.specs.slice(0, 3).map((s) => (
                  <div key={s.label} className="bg-ink/80 p-4">
                    <p className="font-display text-2xl font-extrabold">{s.value}</p>
                    <p className="mt-1 font-mono text-[9px] uppercase tracking-widest text-white/45">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-20 sm:py-28" data-testid="vehicle-details">
        <div className="container-x grid gap-14 lg:grid-cols-12">
          <div className="lg:col-span-7">
            <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Scheda tecnica</p></Reveal>
            <Lines as="h2" lines={["Dati tecnici", "principali."]} className="display mt-3 text-4xl text-ink sm:text-5xl" />
            <div className="mt-8"><SpecTable specs={v.specs} /></div>
            <Reveal className="mt-6 flex flex-wrap gap-2">
              {v.tags.map((t) => (
                <span key={t} className="border border-ink/15 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-ink/60">{t}</span>
              ))}
              {v.units > 1 && <span className="bg-blue-600 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-white">{v.units} unità disponibili</span>}
            </Reveal>
            <Reveal className="mt-10 space-y-1 text-xs text-ink/40"><p>Dati indicativi riferiti alla scheda ufficiale del costruttore. Disponibilità soggetta a conferma.</p>{v.credit && <p data-testid="vehicle-photo-credit">Foto: {v.credit}</p>}</Reveal>
          </div>
          <Reveal delay={0.15} className="lg:col-span-5">
            <div className="sticky top-28 border border-ink/10 bg-paper p-7">
              <p className="eyebrow flex items-center gap-3"><Tiles />Noleggia questo mezzo</p>
              <p className="display mt-3 text-3xl text-ink">Disponibilità e prezzo in pochi minuti</p>
              <p className="mt-3 text-sm text-ink/60">Chiama o scrivi: ti confermiamo subito la disponibilità e organizziamo consegna e ritiro in cantiere.</p>
              <div className="mt-6 flex flex-col gap-3">
                <a href={`tel:${COMPANY.phoneIntl}`} className="btn-dark" data-testid="vehicle-cta-phone"><Phone className="h-4 w-4" /> {COMPANY.phoneDisplay}</a>
                <a href={wa} target="_blank" rel="noopener noreferrer" className="btn-primary" data-testid="vehicle-cta-whatsapp">WhatsApp <ArrowRight className="h-4 w-4" /></a>
                <a href="#preventivo" className="btn-outline" data-testid="vehicle-cta-quote">Compila il preventivo</a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {v.gallery && (
        <section className="bg-paper py-20 sm:py-28" data-testid="vehicle-gallery-section">
          <div className="container-x">
            <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Galleria</p></Reveal>
            <Lines as="h2" lines={["Il mezzo", "da vicino."]} className="display mt-3 text-4xl text-ink sm:text-5xl" />
            <div className="mt-10"><Gallery images={v.gallery} /></div>
          </div>
        </section>
      )}

      <section id="preventivo" className="relative overflow-hidden bg-ink py-20 text-white sm:py-28 grain" data-testid="vehicle-quote-section">
        <div className="absolute inset-0 blueprint opacity-50" />
        <div className="container-x relative grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Reveal y={10}><p className="eyebrow text-blue-500 flex items-center gap-3"><Tiles />Preventivo</p></Reveal>
            <Lines as="h2" lines={["Richiedi un", "preventivo per", v.model]} className="display mt-3 text-4xl sm:text-5xl" />
            <Reveal delay={0.2} className="mt-5 text-white/60"><p>Il mezzo è già selezionato: indica durata e luogo e ti rispondiamo in giornata.</p></Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-8"><QuoteForm defaultEquipment={v.name} /></Reveal>
        </div>
      </section>

      {related.length > 0 && (
        <section className="bg-paper py-20 sm:py-28 blueprint-light" data-testid="vehicle-related">
          <div className="container-x">
            <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <Reveal y={10}><p className="eyebrow flex items-center gap-3"><Tiles />Stessa categoria</p></Reveal>
                <Lines as="h2" lines={["Altri mezzi", cat.name]} className="display mt-3 text-4xl text-ink sm:text-5xl" />
              </div>
              <Reveal><Link to={`/parco-mezzi/${cat.slug}`} className="btn-outline" data-testid="vehicle-back-category"><ArrowLeft className="h-4 w-4" /> Tutti i mezzi {cat.name}</Link></Reveal>
            </div>
            <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => <Item key={r.slug}><VehicleCard vehicle={r} /></Item>)}
            </Stagger>
          </div>
        </section>
      )}
    </>
  );
}
